import { FC } from 'react'
import { List, ListSubheader, CircularProgress } from '@material-ui/core'
import { Error } from '@material-ui/icons'
import { boreholeInfoStyle } from './BoreholeInfo.style'
import { BoreholeFigure } from './boreholefigure/BoreholeFigure'
import { BoreholeLegend } from './boreholelegend/BoreholeLegend'
import { BoreholeMeta } from './boreholemeta/BoreholeMeta'
import { NearestBoreholeObject } from 'src/dataSources/geo/NearestBoreHoles'
import { CallResult } from 'src/CustomTypes'
import { useSelector } from 'react-redux'
import { RootState } from 'src/Store'
import { AddressReverseResult } from 'src/dataSources/dataforsyningen/AddressReverse'
import { FormattedMessage } from 'react-intl'
import { appStyle } from 'src/App.style'

/**
 * BoreholeInfoProps contains properties for the component.
 */
export interface BoreholeInfoProps { // eslint-disable-line @typescript-eslint/no-empty-interface
}

/**
 * BoreholeInfo displays information related to bore holes such as meta data and figures.
 * @param props Properties for component.
 * @returns JSX component.
 */
export const BoreholeInfo: FC<BoreholeInfoProps> = (props: BoreholeInfoProps) => {
  const classes = boreholeInfoStyle()
  const appClasses = appStyle()

  const selectedBorehole = useSelector((state: RootState): NearestBoreholeObject | undefined => state.boreholeInfo.selectedBorehole)
  const fetchBoreholeStatus = useSelector((state: RootState): CallResult => state.boreholeInfo.fetchBoreholeStatus)
  const fetchImageStatus = useSelector((state: RootState): CallResult => state.boreholeInfo.fetchImageStatus)
  const fetchLegendStatus = useSelector((state: RootState): CallResult => state.boreholeInfo.fetchLegendStatus)
  const postalAddress = useSelector((state: RootState): AddressReverseResult | undefined => state.boreholeInfo.addressInfo)

  const fetched = fetchBoreholeStatus === CallResult.Succeeded
  const noBoreholesFound = selectedBorehole === undefined
  const error = fetchBoreholeStatus === CallResult.Failed
  const running = fetchBoreholeStatus === CallResult.Running
  return (
    <>
      <List
        component='nav'
        aria-labelledby='nested-list-subheader'
        subheader={
          <ListSubheader component='div' className={classes.nestedListSubheader} id='nested-list-subheader'>
            <FormattedMessage id='borehole.info' />
            <div className={classes.nestedListSubheaderIcon} />
          </ListSubheader>
        }
      />
      {running && (
        <div className={classes.progress}>
          <CircularProgress size={30} className={appClasses.progressIcon} />
          <FormattedMessage id='common.loading' />
        </div>
      )}
      {error && (
        <div className={appClasses.errorMessage}>
          <Error fontSize='large' className={appClasses.errorIcon} />
          <FormattedMessage id='common.loading.error' />
        </div>
      )}
      {fetched && noBoreholesFound && (
        <div className={classes.noBoreholes}>
          <Error fontSize='large' className={appClasses.errorIcon} />
          <FormattedMessage id='borehole.info.noboreholes' />
        </div>
      )}
      {fetched && !noBoreholesFound && (
        <>
          <BoreholeMeta selectedBorehole={selectedBorehole} postalAddress={postalAddress} />
          <List
            component='nav'
            aria-labelledby='nested-list-subheader'
            subheader={
              <ListSubheader component='div' className={classes.nestedListSubheader} id='nested-list-subheader'>
                <FormattedMessage id='borehole.info.figure' />
                <div className={classes.nestedListSubheaderIcon} />
              </ListSubheader>
            }
          />
          {(fetchImageStatus === CallResult.Running || fetchLegendStatus === CallResult.Running) && (
            <div className={classes.progress}>
              <CircularProgress size={30} className={appClasses.progressIcon} />
              <FormattedMessage id='common.loading' />
            </div>
          )}
          {fetchImageStatus === CallResult.Succeeded && fetchLegendStatus === CallResult.Succeeded && (
            <div className={classes.figure}>
              <BoreholeFigure selectedBorehole={selectedBorehole} />
              <BoreholeLegend />
            </div>
          )}
        </>
      )}
    </>
  )
}
